import { useState, useEffect, useRef } from "react";
import {
  Shield,
  Plus,
  Edit2,
  Trash2,
  X,
  CheckCircle,
  AlertCircle,
  MapPin,
} from "lucide-react";
import { MapContainer, TileLayer, Polygon, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useAuthStore } from "../../state/auth";
import { cn } from "../../lib/utils";
import { useTranslation } from "../../i18n";
import type { LangCode } from "../../i18n";

// Fix Leaflet default marker icon (use CDN to avoid Vite asset import issues)
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

interface Geofence {
  id: string;
  name: string;
  hospital_id: string;
  hospital_name: string;
  polygon: Array<{ lat: number; lng: number }>;
  is_active: boolean;
  created_by: string;
  created_at: string;
}

interface Hospital {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
}

const ATLANTA_CENTER: [number, number] = [33.78, -84.38];

export function GeofenceManager() {
  const { t, lang } = useTranslation();
  const { user } = useAuthStore();
  const [geofences, setGeofences] = useState<Geofence[]>([]);
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingGeofence, setEditingGeofence] = useState<Geofence | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [selectedGeofence, setSelectedGeofence] = useState<Geofence | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchGeofences = async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ role: user.role });
      if (user.hospital_id) params.append("hospital_id", user.hospital_id);
      if (user.main_site_id) params.append("main_site_id", user.main_site_id);

      const response = await fetch(`/api/geofences?${params}`, {
        headers: getAuthHeaders(user),
      });

      if (response.ok) {
        const data = await response.json();
        // Ensure polygon is parsed (JSONB may come as string or object)
        const parsed = data.map((g: any) => ({
          ...g,
          polygon: typeof g.polygon === 'string' ? JSON.parse(g.polygon) : g.polygon,
        }));
        setGeofences(parsed);
      } else {
        setError(t("geofence.failedLoad", "Failed to load geofences"));
      }
    } catch (err) {
      console.error("Failed to fetch geofences:", err);
      setError(t("geofence.failedLoad", "Failed to load geofences"));
    } finally {
      setLoading(false);
    }
  };

  const fetchHospitals = async () => {
    if (!user) return;
    try {
      const params = new URLSearchParams({ role: user.role });
      if (user.hospital_id) params.append("hospital_id", user.hospital_id);
      if (user.main_site_id) params.append("main_site_id", user.main_site_id);

      const response = await fetch(`/api/hospitals?${params.toString()}`, {
        headers: getAuthHeaders(user),
      });

      if (response.ok) {
        const data = await response.json();
        setHospitals(data);
      }
    } catch (err) {
      console.error("Failed to fetch hospitals:", err);
    }
  };

  useEffect(() => {
    fetchGeofences();
    fetchHospitals();
  }, [user]);

  const handleDelete = async (id: string) => {
    if (!user) return;
    try {
      const response = await fetch(`/api/geofences?id=${id}`, {
        method: "DELETE",
        headers: getAuthHeaders(user),
      });

      if (response.ok) {
        setGeofences(geofences.filter((g) => g.id !== id));
        setDeleteConfirm(null);
      } else {
        setError(t("geofence.failedDelete", "Failed to delete geofence"));
      }
    } catch (err) {
      console.error("Failed to delete geofence:", err);
      setError(t("geofence.failedDelete", "Failed to delete geofence"));
    }
  };

  const canCreate = user?.role === "ADMIN" || user?.role === "Administrator" || user?.role === "SUPERVISOR";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-50 text-blue-600">
            <Shield className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {t("geofence.management", "Geofence Management")}
            </h2>
            <p className="text-sm text-gray-600">
              {t("geofence.managementSubtitle", "Define secure zones and monitor boundary violations")}
            </p>
          </div>
        </div>
        {canCreate && (
          <button
            onClick={() => {
              setEditingGeofence(null);
              setShowModal(true);
            }}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            {t("geofence.create", "Create Geofence")}
          </button>
        )}
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-gap-2">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Geofences List */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : geofences.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 text-center">
            <Shield className="h-12 w-12 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900">
              {t("geofence.noneYet", "No geofences yet")}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {canCreate
                ? t("geofence.noneYetCanCreate", "Create your first geofence to get started with boundary monitoring.")
                : t("geofence.noneConfigured", "No geofences are configured for your location.")}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    {t("common.name", "Name")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    {t("domain.hospital", "Hospital")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    {t("common.status", "Status")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    {t("geofence.createdBy", "Created By")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    {t("geofence.createdDate", "Created Date")}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    {t("common.actions", "Actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {geofences.map((geofence) => (
                  <tr key={geofence.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <button
                        onClick={() => setSelectedGeofence(geofence)}
                        className="font-medium text-gray-900 hover:text-blue-600 transition-colors"
                      >
                        {geofence.name}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {geofence.hospital_name}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={cn(
                          "inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold",
                          geofence.is_active
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-gray-100 text-gray-600"
                        )}
                      >
                        {geofence.is_active ? (
                          <CheckCircle className="h-3 w-3" />
                        ) : (
                          <AlertCircle className="h-3 w-3" />
                        )}
                        {geofence.is_active ? t("status.active", "Active") : t("status.inactive", "Inactive")}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {geofence.created_by}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {formatDate(geofence.created_at, lang)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {canCreate && (
                          <>
                            <button
                              onClick={() => {
                                setEditingGeofence(geofence);
                                setShowModal(true);
                              }}
                              className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title={t("common.edit", "Edit")}
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(geofence.id)}
                              className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title={t("common.delete", "Delete")}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Preview Map */}
      {selectedGeofence && (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">
              {selectedGeofence.name} - {t("geofence.preview", "Preview")}
            </h3>
            <button
              onClick={() => setSelectedGeofence(null)}
              className="p-1 text-gray-500 hover:text-gray-700"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="h-64 rounded-lg overflow-hidden">
            <MapContainer attributionControl={false}
              center={ATLANTA_CENTER}
              zoom={11}
              style={{ height: "100%", width: "100%" }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution=""
              />
              {selectedGeofence.polygon.length > 0 && (
                <Polygon
                  positions={selectedGeofence.polygon.map((p) => [
                    p.lat,
                    p.lng,
                  ])}
                  color={
                    selectedGeofence.is_active
                      ? "#10b981"
                      : "#9ca3af"
                  }
                  fillColor={
                    selectedGeofence.is_active
                      ? "#10b981"
                      : "#9ca3af"
                  }
                  fillOpacity={0.3}
                />
              )}
            </MapContainer>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <GeofenceModal
          geofence={editingGeofence}
          t={t}
          hospitals={hospitals}
          userRole={user?.role}
          userHospitalId={user?.hospital_id}
          onSave={async (data) => {
            try {
              if (editingGeofence) {
                // Edit = delete old + create new (single endpoint, no PUT support)
                await fetch(`/api/geofences?id=${editingGeofence.id}`, {
                  method: "DELETE",
                  headers: getAuthHeaders(user),
                });
                const response = await fetch("/api/geofences", {
                  method: "POST",
                  headers: getAuthHeaders(user),
                  body: JSON.stringify(data),
                });
                if (response.ok) {
                  await fetchGeofences();
                  setShowModal(false);
                } else {
                  setError(t("geofence.failedUpdate", "Failed to update geofence"));
                }
              } else {
                const response = await fetch("/api/geofences", {
                  method: "POST",
                  headers: getAuthHeaders(user),
                  body: JSON.stringify(data),
                });
                if (response.ok) {
                  await fetchGeofences();
                  setShowModal(false);
                } else {
                  setError(t("geofence.failedCreate", "Failed to create geofence"));
                }
              }
            } catch (err) {
              console.error("Failed to save geofence:", err);
              setError(t("geofence.failedSave", "Failed to save geofence"));
            }
          }}
          onClose={() => {
            setShowModal(false);
            setEditingGeofence(null);
          }}
        />
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="rounded-xl bg-white p-6 max-w-sm shadow-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t("geofence.deleteConfirmTitle", "Delete Geofence?")}
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              {t("geofence.deleteConfirmBody", "This action cannot be undone. The geofence will be permanently deleted.")}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2 rounded-lg border border-gray-200 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
              >
                {t("common.cancel", "Cancel")}
              </button>
              <button
                onClick={() => {
                  if (deleteConfirm) handleDelete(deleteConfirm);
                }}
                className="flex-1 px-4 py-2 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 transition-colors"
              >
                {t("common.delete", "Delete")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface GeofenceModalProps {
  geofence: Geofence | null;
  t: (key: string, fallback?: string) => string;
  hospitals: Hospital[];
  userRole?: string;
  userHospitalId?: string | null;
  onSave: (data: {
    name: string;
    hospital_id: string;
    polygon: Array<{ lat: number; lng: number }>;
  }) => Promise<void>;
  onClose: () => void;
}

function GeofenceModal({
  geofence,
  t,
  hospitals,
  userRole,
  userHospitalId,
  onSave,
  onClose,
}: GeofenceModalProps) {
  const [name, setName] = useState(geofence?.name || "");
  const [hospitalId, setHospitalId] = useState(
    geofence?.hospital_id ||
      (userRole === "SUPERVISOR" ? userHospitalId : "") ||
      ""
  );
  const [polygon, setPolygon] = useState<Array<{ lat: number; lng: number }>>(
    geofence?.polygon || []
  );
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) {
      alert(t("geofence.enterName", "Please enter a geofence name"));
      return;
    }
    if (!hospitalId) {
      alert(t("geofence.selectHospitalAlert", "Please select a hospital"));
      return;
    }
    if (polygon.length < 3) {
      alert(t("geofence.minPoints", "Polygon must have at least 3 points"));
      return;
    }

    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        hospital_id: hospitalId,
        polygon,
      });
    } finally {
      setSaving(false);
    }
  };

  const isSupervisor = userRole === "SUPERVISOR";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="rounded-xl bg-white max-w-2xl w-full max-h-[90vh] flex flex-col shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {geofence ? t("geofence.edit", "Edit Geofence") : t("geofence.create", "Create Geofence")}
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-500 hover:text-gray-700"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-6 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              {t("geofence.nameLabel", "Geofence Name")}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("geofence.namePlaceholder", "e.g., Main Entrance, Parking Lot A")}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            />
          </div>

          {/* Hospital Selector */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              {t("domain.hospital", "Hospital")}
            </label>
            <select
              value={hospitalId}
              onChange={(e) => setHospitalId(e.target.value)}
              disabled={isSupervisor}
              className={cn(
                "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none",
                isSupervisor && "bg-gray-100 text-gray-500"
              )}
            >
              <option value="">{t("geofence.selectHospitalOption", "Select a hospital...")}</option>
              {hospitals.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.name}
                </option>
              ))}
            </select>
          </div>

          {/* Map */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              {t("geofence.drawPolygon", "Draw Polygon on Map")}
            </label>
            <p className="text-xs text-gray-600 mb-3">
              {t("geofence.drawPolygonHint", "Click on the map to add points. Polygon must have at least 3 points. Current points: {n}").replace("{n}", String(polygon.length))}
            </p>
            <div className="h-64 rounded-lg border border-gray-300 overflow-hidden">
              <MapContainer attributionControl={false}
                center={ATLANTA_CENTER}
                zoom={11}
                style={{ height: "100%", width: "100%" }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution=""
                />
                {polygon.length > 0 && (
                  <Polygon
                    positions={polygon.map((p) => [p.lat, p.lng])}
                    color="#3b82f6"
                    fillColor="#3b82f6"
                    fillOpacity={0.2}
                  />
                )}
                <MapClickHandler polygon={polygon} setPolygon={setPolygon} />
              </MapContainer>
            </div>
          </div>

          {/* Polygon Preview */}
          {polygon.length > 0 && (
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-sm text-gray-900">
                  {t("geofence.polygonPoints", "Polygon Points ({n})").replace("{n}", String(polygon.length))}
                </h4>
                <button
                  onClick={() => setPolygon([])}
                  className="text-xs px-3 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
                >
                  {t("common.clear", "Clear")}
                </button>
              </div>
              <div className="max-h-24 overflow-y-auto">
                <div className="text-xs space-y-1">
                  {polygon.map((point, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-2 bg-white rounded border border-gray-200"
                    >
                      <span className="text-gray-600">
                        {t("geofence.pointN", "Point {n}:").replace("{n}", String(idx + 1))} {point.lat.toFixed(5)},{" "}
                        {point.lng.toFixed(5)}
                      </span>
                      <button
                        onClick={() =>
                          setPolygon(polygon.filter((_, i) => i !== idx))
                        }
                        className="text-red-600 hover:text-red-700"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 rounded-lg border border-gray-200 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
          >
            {t("common.cancel", "Cancel")}
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !name.trim() || !hospitalId || polygon.length < 3}
            className="flex-1 px-4 py-2 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? t("common.saving", "Saving...") : t("geofence.save", "Save Geofence")}
          </button>
        </div>
      </div>
    </div>
  );
}

function MapClickHandler({
  polygon,
  setPolygon,
}: {
  polygon: Array<{ lat: number; lng: number }>;
  setPolygon: (polygon: Array<{ lat: number; lng: number }>) => void;
}) {
  useMapEvents({
    click: (e) => {
      const newPoint = { lat: e.latlng.lat, lng: e.latlng.lng };
      setPolygon([...polygon, newPoint]);
    },
  });

  return null;
}

function getAuthHeaders(user: any) {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const token = localStorage.getItem("auth_token");
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (user?.email) headers["x-user-email"] = user.email;
  return headers;
}

function formatDate(dateStr: string, lang: LangCode) {
  return new Intl.DateTimeFormat(lang, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(dateStr));
}
